import { useCallback, useState } from 'react';

export type ReasonDialogType = 'dispute' | 'invoicing_hold' | 'cancel' | 'reopen';

function hasExistingVehicleLocation(currentLocation: string | null | undefined): boolean {
  return currentLocation != null && currentLocation !== '';
}

export interface WorkOrderDetailDialogs {
  // Notes
  notesDialogOpen: boolean;
  setNotesDialogOpen: (open: boolean) => void;

  // Schedule bar edit mode (#2105) — kept here so switching work order resets it
  scheduleEditOpen: boolean;
  setScheduleEditOpen: (open: boolean) => void;

  // Confirmation method — asked on → confirmed only. Arrival infers in_person (#2334).
  confirmMethodOpen: boolean;
  setConfirmMethodOpen: (open: boolean) => void;

  // Vehicle location prompt after vehicle_arrived (#2116)
  locationDialogOpen: boolean;
  setLocationDialogOpen: (open: boolean) => void;
  openVehicleLocationDialogIfNeeded: (currentLocation: string | null | undefined) => void;

  // Accept quote (#1023) — scheduling dialog shown before quote -> pending
  acceptQuoteDialogOpen: boolean;
  setAcceptQuoteDialogOpen: (open: boolean) => void;

  // Pause
  pauseDialogOpen: boolean;
  setPauseDialogOpen: (open: boolean) => void;

  // Undo
  undoDialogOpen: boolean;
  setUndoDialogOpen: (open: boolean) => void;

  // Invoice
  invoiceDialogOpen: boolean;
  setInvoiceDialogOpen: (open: boolean) => void;

  // Reason (dispute / invoicing hold / cancel)
  reasonDialogOpen: boolean;
  setReasonDialogOpen: (open: boolean) => void;
  reasonDialogType: ReasonDialogType;
  setReasonDialogType: (type: ReasonDialogType) => void;
  reasonText: string;
  setReasonText: (value: string) => void;

  // Mileage required
  mileageDialogOpen: boolean;
  setMileageDialogOpen: (open: boolean) => void;
  pendingServiceCompletion: string | null;
  setPendingServiceCompletion: (id: string | null) => void;
  pendingWorkOrderCompletion: boolean;
  setPendingWorkOrderCompletion: (pending: boolean) => void;
  mileageDialogLoading: boolean;
  setMileageDialogLoading: (loading: boolean) => void;

  // Service-book invoice override (#2340)
  serviceBookOverrideOpen: boolean;
  setServiceBookOverrideOpen: (open: boolean) => void;
  serviceBookOverrideReason: string;
  setServiceBookOverrideReason: (value: string) => void;

  // Close every dialog and clear its transient fields. Used when the detail
  // view switches to a different work order so a dialog (and its pending
  // action target) from the previous one does not leak across (issue #420).
  reset: () => void;
}

export function useWorkOrderDetailDialogs(): WorkOrderDetailDialogs {
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [scheduleEditOpen, setScheduleEditOpen] = useState(false);

  const [confirmMethodOpen, setConfirmMethodOpen] = useState(false);

  const [locationDialogOpen, setLocationDialogOpen] = useState(false);

  const [acceptQuoteDialogOpen, setAcceptQuoteDialogOpen] = useState(false);

  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [undoDialogOpen, setUndoDialogOpen] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);

  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [reasonDialogType, setReasonDialogType] = useState<ReasonDialogType>('dispute');
  const [reasonText, setReasonText] = useState('');

  const [mileageDialogOpen, setMileageDialogOpen] = useState(false);
  const [pendingServiceCompletion, setPendingServiceCompletion] = useState<string | null>(null);
  const [pendingWorkOrderCompletion, setPendingWorkOrderCompletion] = useState(false);
  const [mileageDialogLoading, setMileageDialogLoading] = useState(false);

  const [serviceBookOverrideOpen, setServiceBookOverrideOpen] = useState(false);
  const [serviceBookOverrideReason, setServiceBookOverrideReason] = useState('');

  const openVehicleLocationDialogIfNeeded = useCallback((currentLocation: string | null | undefined) => {
    if (hasExistingVehicleLocation(currentLocation)) {
      return;
    }
    setLocationDialogOpen(true);
  }, []);

  const reset = useCallback(() => {
    setNotesDialogOpen(false);
    setScheduleEditOpen(false);
    setConfirmMethodOpen(false);
    setLocationDialogOpen(false);
    setAcceptQuoteDialogOpen(false);
    setPauseDialogOpen(false);
    setUndoDialogOpen(false);
    setInvoiceDialogOpen(false);
    setReasonDialogOpen(false);
    setReasonDialogType('dispute');
    setReasonText('');
    setMileageDialogOpen(false);
    setPendingServiceCompletion(null);
    setPendingWorkOrderCompletion(false);
    setMileageDialogLoading(false);
    setServiceBookOverrideOpen(false);
    setServiceBookOverrideReason('');
  }, []);

  return {
    notesDialogOpen,
    setNotesDialogOpen,
    scheduleEditOpen,
    setScheduleEditOpen,
    confirmMethodOpen,
    setConfirmMethodOpen,
    locationDialogOpen,
    setLocationDialogOpen,
    openVehicleLocationDialogIfNeeded,
    acceptQuoteDialogOpen,
    setAcceptQuoteDialogOpen,
    pauseDialogOpen,
    setPauseDialogOpen,
    undoDialogOpen,
    setUndoDialogOpen,
    invoiceDialogOpen,
    setInvoiceDialogOpen,
    reasonDialogOpen,
    setReasonDialogOpen,
    reasonDialogType,
    setReasonDialogType,
    reasonText,
    setReasonText,
    mileageDialogOpen,
    setMileageDialogOpen,
    pendingServiceCompletion,
    setPendingServiceCompletion,
    pendingWorkOrderCompletion,
    setPendingWorkOrderCompletion,
    mileageDialogLoading,
    setMileageDialogLoading,
    serviceBookOverrideOpen,
    setServiceBookOverrideOpen,
    serviceBookOverrideReason,
    setServiceBookOverrideReason,
    reset,
  };
}
